import { useQuery } from '@apollo/client/react';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useFunds } from 'src/context';
import { parseCurrencyInput } from 'src/helpers/currency';
import { GetPortfolioData } from '~/graphql';
import { useFormData } from '~/hooks';
import { GetPortfolioDataResponse, PortfolioDataInput } from '~/types';
import { formatCurrency, getReportingPeriod } from '~/utils';

export type SelectedFund = {
    name: string;
    citicode: string;
    allocation: number;
    amc: number;
};

export const usePerformanceChart = (reportingPeriodYears: number) => {
    const { selectedFunds, setPortfolioData } = useFunds();
    const netAllocation = 100;
    const { formData } = useFormData();

    const {
        drawdown,
        drawdownType,
        drawdownAmount,
        drawdownPercent,
        contributions,
        contributionsAmount,
        initialAdviserCharge_type,
        initialAdviserCharge_amount,
        initialAdviserCharge_percent,
        ongoingAdviserCharge_type,
        ongoingAdviserCharge_amount,
        ongoingAdviserCharge_percent,
    } = formData;

    const hasDrawdown = drawdown === 'Yes';
    const hasContributions = contributions === 'Yes';

    const investmentAmount = parseCurrencyInput(formData.investmentAmount as string);
    const drawdownMonetaryAmount = hasContributions
        ? parseCurrencyInput(contributionsAmount as string) * -1
        : hasDrawdown
          ? parseCurrencyInput(drawdownAmount as string)
          : 0;

    const { startDate, endDate } = getReportingPeriod(reportingPeriodYears);

    const indefiniteArticle =
        [8, 11, 18, 80, 81, 82, 83, 84, 85, 86, 87, 88, 89].indexOf(reportingPeriodYears) !== -1 ? 'an' : 'a';

    const chartTitle = `£${formatCurrency(formData.investmentAmount as string)} invested in ${dayjs(startDate).format(
        'MMMM YYYY'
    )}`;
    let chartSubTitle = `over ${indefiniteArticle} ${reportingPeriodYears} year period`;

    if (hasContributions) {
        chartSubTitle = `${chartSubTitle} with a £${formatCurrency(contributionsAmount as string)} contribution`;
    }

    if (hasDrawdown) {
        if (drawdownType === 'percent' && Number(drawdownPercent) > 0) {
            chartSubTitle = `${chartSubTitle} with ${drawdownPercent}% drawdown`;
        }
        if (drawdownType === 'amount' && Number(drawdownAmount) > 0) {
            chartSubTitle = `${chartSubTitle} with a £${formatCurrency(drawdownAmount as string)} drawdown`;
        }
    }

    const suffix = `(net of fund Annual Management Charge)`;

    chartSubTitle += ` ${suffix}`;

    const variables = useMemo(() => {
        const funds = selectedFunds.map(({ citicode, allocation }) => ({
            citicode,
            allocation,
        }));

        const initialAdviserCharge =
            initialAdviserCharge_type === 'amount'
                ? { type: 'amount', value: parseCurrencyInput(initialAdviserCharge_amount as string) }
                : { type: 'percent', value: parseCurrencyInput(initialAdviserCharge_percent as string, 'percent') };

        const ongoingAdviserCharge =
            ongoingAdviserCharge_type === 'amount'
                ? { type: 'amount', value: parseCurrencyInput(ongoingAdviserCharge_amount as string) }
                : { type: 'percent', value: parseCurrencyInput(ongoingAdviserCharge_percent as string, 'percent') };

        let cleanedInput = {
            funds,
            startDate,
            endDate,
            investmentAmount,
            drawdownPercent,
            drawdownMonetaryAmount,
            netAllocation,
            initialAdviserCharge,
            ongoingAdviserCharge,
        };

        if (hasDrawdown) {
            if (drawdownType === 'percent') {
                cleanedInput = { ...cleanedInput, drawdownMonetaryAmount: 0 };
            } else if (drawdownType === 'amount') {
                cleanedInput = { ...cleanedInput, drawdownPercent: 0 };
            }
        } else if (hasContributions) {
            cleanedInput = { ...cleanedInput, drawdownPercent: 0 };
        } else {
            cleanedInput = { ...cleanedInput, drawdownMonetaryAmount: 0, drawdownPercent: 0 };
        }

        return { input: cleanedInput };
    }, [
        selectedFunds,
        startDate,
        endDate,
        investmentAmount,
        drawdownPercent,
        drawdownAmount,
        netAllocation,
        drawdownType,
        reportingPeriodYears,
        initialAdviserCharge_type,
        initialAdviserCharge_amount,
        initialAdviserCharge_percent,
        ongoingAdviserCharge_type,
        ongoingAdviserCharge_amount,
        ongoingAdviserCharge_percent,
    ]);

    useEffect(() => {
        setPortfolioData(variables.input as PortfolioDataInput);
    }, [variables, setPortfolioData]);

    const { data, loading: isLoading } = useQuery<GetPortfolioDataResponse>(GetPortfolioData, {
        variables,
        fetchPolicy: 'no-cache',
    });

    const categories = data?.getPortfolioData?.seriesData.map(({ date }) => {
        return date.split('/').reverse().join('-');
    });
    const values = data?.getPortfolioData?.seriesData.map(({ value }) => value).map(Number);

    return {
        chartTitle,
        chartSubTitle,
        categories,
        values,
        isLoading,
    };
};
